import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Divider,
  Alert,
  IconButton,
  Tooltip,
  LinearProgress
} from '@mui/material';
import {
  Timeline,
  Restaurant,
  Assignment,
  People,
  Settings,
  Add,
  Edit,
  Visibility,
  TrendingUp,
  Warning
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { get } from '../../config/axios';

const LowFodmapDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalUsers: 0,
    eliminationUsers: 0,
    introductionUsers: 0,
    maintenanceUsers: 0,
    testingFoods: 0,
    activePrompts: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      const response = await get('/api/dashboard/low-fodmap/stats');
      if (response.success) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Error fetching Low FODMAP stats:', error);
      setError('Failed to load dashboard statistics');
    } finally {
      setLoading(false);
    }
  };

  const dashboardCards = [
    {
      title: 'Total Low FODMAP Users',
      value: stats.totalUsers,
      icon: <People color="primary" />,
      color: 'primary.main',
      description: 'Users following Low FODMAP diet'
    },
    {
      title: 'Elimination Phase',
      value: stats.eliminationUsers,
      icon: <Restaurant color="error" />,
      color: 'error.main',
      description: 'Users in elimination phase'
    },
    {
      title: 'Introduction Phase',
      value: stats.introductionUsers,
      icon: <Timeline color="warning" />,
      color: 'warning.main',
      description: 'Users in testing phase'
    },
    {
      title: 'Maintenance Phase',
      value: stats.maintenanceUsers,
      icon: <TrendingUp color="success" />,
      color: 'success.main',
      description: 'Users in maintenance phase'
    }
  ];

  const quickActions = [
    {
      title: 'Manage Testing Foods',
      description: 'Configure FODMAP testing schedule and portions',
      icon: <Restaurant />,
      action: () => navigate('/diet/low-fodmap/testing-foods'),
      color: 'primary'
    },
    {
      title: 'Phase-Specific Prompts',
      description: 'Manage prompts for each Low FODMAP phase',
      icon: <Assignment />,
      action: () => navigate('/diet/low-fodmap/prompts'),
      color: 'secondary'
    },
    {
      title: 'Phase Transitions',
      description: 'Configure automatic phase transition rules',
      icon: <Timeline />,
      action: () => navigate('/diet/low-fodmap/transitions'),
      color: 'info'
    },
    {
      title: 'User Progress Tracking',
      description: 'Monitor user progress through phases',
      icon: <People />,
      action: () => navigate('/diet/low-fodmap/progress'),
      color: 'success'
    }
  ];

  if (loading) {
    return (
      <Box sx={{ width: '100%' }}>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          🌱 Low FODMAP Diet Management
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Comprehensive management of Low FODMAP diet plans, phases, and testing protocols
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {dashboardCards.map((card, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ color: card.color, mr: 2 }}>
                    {card.icon}
                  </Box>
                  <Typography variant="h6" component="div">
                    {card.title}
                  </Typography>
                </Box>
                <Typography variant="h3" component="div" sx={{ color: card.color, mb: 1 }}>
                  {card.value}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {card.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Quick Actions */}
      <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 3 }}>
        🚀 Quick Actions
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {quickActions.map((action, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card 
              sx={{ 
                height: '100%', 
                cursor: 'pointer',
                '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 }
              }}
              onClick={action.action}
            >
              <CardContent sx={{ textAlign: 'center' }}>
                <Box sx={{ color: `${action.color}.main`, mb: 2 }}>
                  {action.icon}
                </Box>
                <Typography variant="h6" component="div" gutterBottom>
                  {action.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {action.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* System Status */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" component="h3" gutterBottom>
            🔧 System Status
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Chip 
                  label={`${stats.testingFoods} Testing Foods`}
                  color="primary"
                  variant="outlined"
                  sx={{ mr: 2 }}
                />
                <Typography variant="body2">
                  FODMAP testing foods configured
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Chip 
                  label={`${stats.activePrompts} Active Prompts`}
                  color="secondary"
                  variant="outlined"
                  sx={{ mr: 2 }}
                />
                <Typography variant="body2">
                  Phase-specific prompts active
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Information Alert */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Low FODMAP Diet Management:</strong> This system automatically manages the three phases 
          (Elimination, Introduction, Maintenance) with structured food testing protocols. Users progress 
          through phases based on time and symptom monitoring.
        </Typography>
      </Alert>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
};

export default LowFodmapDashboard;
